import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { PageHeader, inr, fmtDate } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil, Play, FileDown, FileSpreadsheet, CheckCircle2, Check, X, SlidersHorizontal } from "lucide-react";
import { exportPdf, exportExcel, downloadSalarySlip } from "@/lib/feeExport";

const SETUP_FIELDS = [["basic", "Basic salary"], ["allowances", "Allowances"], ["incentives", "Incentives"], ["overtime", "Overtime"], ["attendance_deduction", "Attendance deduction"], ["loan_deduction", "Loan deduction (EMI)"], ["free_leaves", "Free leaves / month"], ["leave_deduction_per_day", "Leave deduction per day (blank = basic ÷ 30)"]];
const thisMonth = () => new Date().toISOString().slice(0, 7);

function SetupDialog({ staff, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { if (staff) setF(staff.setup || { basic: 0, allowances: 0, incentives: 0, overtime: 0, attendance_deduction: 0, loan_deduction: 0, free_leaves: 1, leave_deduction_per_day: "" }); }, [staff]);
  const n = (k) => Number(f[k]) || 0;
  async function save() {
    try {
      const body = Object.fromEntries(SETUP_FIELDS.map(([k]) => [k, k === "leave_deduction_per_day" && (f[k] === "" || f[k] == null) ? null : n(k)]));
      await api.put(`/hr/salary/${staff.id}`, body); toast.success("Salary saved"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <Dialog open={!!staff} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle className="font-display">Salary setup • {staff?.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {SETUP_FIELDS.map(([k, l]) => <div key={k}><Label className="text-xs">{l}</Label><Input data-testid={`hr-setup-${k}`} type="number" min={0} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" /></div>)}
        </div>
        <div className="text-sm rounded-xl bg-violet-50 p-3">Monthly net before leave & advances: <b>{inr(n("basic") + n("allowances") + n("incentives") + n("overtime") - n("attendance_deduction") - n("loan_deduction"))}</b></div>
        <Button data-testid="hr-setup-save" onClick={save} className="bg-violet-600 hover:bg-violet-700">Save salary</Button>
      </DialogContent>
    </Dialog>
  );
}

function SlipActions({ p, onDone }) {
  const [mode, setMode] = useState(null);
  const [adj, setAdj] = useState({});
  const [pay, setPay] = useState({ method: "bank", date: new Date().toISOString().slice(0, 10), reference: "" });
  useEffect(() => { setAdj({ incentives: p.incentives, overtime: p.overtime, attendance_deduction: p.attendance_deduction }); }, [p]);
  async function saveAdj() {
    try { await api.patch(`/hr/payslips/${p.id}`, Object.fromEntries(Object.entries(adj).map(([k, v]) => [k, Number(v) || 0]))); toast.success("Updated"); setMode(null); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function markPaid() {
    try { await api.post(`/hr/payslips/${p.id}/pay`, pay); toast.success("Marked paid"); setMode(null); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <>
      <div className="flex gap-1 justify-end">
        {p.status === "pending" && <Button data-testid={`hr-adjust-${p.id}`} size="sm" variant="outline" onClick={() => setMode("adj")}><SlidersHorizontal size={14} /></Button>}
        {p.status === "pending" && <Button data-testid={`hr-pay-${p.id}`} size="sm" variant="outline" onClick={() => setMode("pay")}><CheckCircle2 size={14} className="mr-1" /> Pay</Button>}
        <Button data-testid={`hr-slip-${p.id}`} size="sm" variant="outline" onClick={() => downloadSalarySlip(p.id).catch((e) => toast.error(formatApiError(e)))}><FileDown size={14} /></Button>
      </div>
      <Dialog open={!!mode} onOpenChange={(v) => { if (!v) setMode(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{mode === "pay" ? "Mark salary paid" : "Adjust this month"} • {p.name}</DialogTitle></DialogHeader>
          {mode === "adj" ? (
            <div className="space-y-3">
              {[["incentives", "Incentives"], ["overtime", "Overtime"], ["attendance_deduction", "Attendance deduction"]].map(([k, l]) => <div key={k}><Label>{l}</Label><Input type="number" min={0} value={adj[k]} onChange={(e) => setAdj({ ...adj, [k]: e.target.value })} className="mt-1" /></div>)}
              <Button data-testid="hr-adjust-save" onClick={saveAdj} className="w-full bg-violet-600 hover:bg-violet-700">Save</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-sm">Net salary <b>{inr(p.net)}</b></div>
              <div><Label>Method</Label>
                <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
                  <SelectTrigger data-testid="hr-pay-method" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{["bank", "cash", "upi", "cheque"].map((m) => <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Payment date</Label><Input data-testid="hr-pay-date" type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} className="mt-1" /></div>
              <div><Label>Reference</Label><Input value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} className="mt-1" /></div>
              <Button data-testid="hr-pay-save" onClick={markPaid} className="w-full bg-emerald-600 hover:bg-emerald-700">Mark paid</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

const REPORTS = {
  monthly: { title: "Monthly payroll report", cols: [["name", "Staff"], ["role", "Role"], ["basic", "Basic", 1], ["allowances", "Allow.", 1], ["incentives", "Incent.", 1], ["overtime", "OT", 1], ["total_deductions", "Deduct.", 1], ["advance_deduction", "Advance", 1], ["net", "Net", 1], ["status", "Status"], ["payment_date", "Paid on"], ["payment_method", "Method"]] },
  yearly: { title: "Yearly salary report", cols: [["name", "Staff"], ["role", "Role"], ["months", "Months"], ["gross", "Gross", 1], ["deductions", "Deductions", 1], ["advances", "Advances", 1], ["net", "Net", 1], ["paid", "Paid", 1]] },
  attendance: { title: "Staff attendance report", cols: [["name", "Staff"], ["role", "Role"], ["working_days", "Working days"], ["marked_days", "Days marked"], ["present_days", "Present days"], ["absent_days", "Absent"], ["half_days", "Half days"], ["late_days", "Late"], ["leave_days", "Approved leave"], ["pending_requests", "Pending leave"], ["rejected_requests", "Rejected leave"]] },
};

function Reports() {
  const [kind, setKind] = useState("monthly");
  const [month, setMonth] = useState(thisMonth());
  const [rows, setRows] = useState([]);
  const def = REPORTS[kind];
  const cols = def.cols.map(([key, label, money]) => ({ key, label, money: !!money }));
  useEffect(() => {
    const params = kind === "yearly" ? { year: month.slice(0, 4) } : { month };
    api.get(`/hr/reports/${kind}`, { params }).then((r) => setRows(r.data.map((x) => ({ ...x, role: ROLE_LABELS[x.role] || x.role })))).catch(() => setRows([]));
  }, [kind, month]);
  const sub = kind === "yearly" ? `Year ${month.slice(0, 4)}` : month;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3 items-end">
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger data-testid="hr-report-kind" className="w-56 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(REPORTS).map(([k, v]) => <SelectItem key={k} value={k}>{v.title}</SelectItem>)}</SelectContent>
        </Select>
        <Input data-testid="hr-report-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 bg-white" />
        <div className="ml-auto flex gap-2">
          <Button data-testid="hr-report-pdf" variant="outline" disabled={!rows.length} onClick={() => exportPdf(def.title, cols, rows, sub)}><FileDown size={15} className="mr-1" /> PDF</Button>
          <Button data-testid="hr-report-excel" variant="outline" disabled={!rows.length} onClick={() => exportExcel(def.title, cols, rows)}><FileSpreadsheet size={15} className="mr-1" /> Excel</Button>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-violet-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-violet-50"><tr>{cols.map((c) => <th key={c.key} className={`px-3 py-2.5 ${c.money ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && <tr><td colSpan={cols.length} className="py-8 text-center text-slate-500">No data</td></tr>}
            {rows.map((r, i) => <tr key={i}>{cols.map((c) => <td key={c.key} className={`px-3 py-2 ${c.money ? "text-right" : ""}`}>{c.money ? inr(r[c.key]) : r[c.key] ?? "—"}</td>)}</tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function HRPayrollPage() {
  const { user } = useAuth();
  const canSetup = ["master_admin", "super_admin", "hr_manager"].includes(user?.role);
  const canDecide = ["master_admin", "super_admin"].includes(user?.role);
  const [staff, setStaff] = useState([]);
  const [editing, setEditing] = useState(null);
  const [month, setMonth] = useState(thisMonth());
  const [slips, setSlips] = useState([]);
  const [advances, setAdvances] = useState([]);

  const loadStaff = () => api.get("/hr/staff").then((r) => setStaff(r.data)).catch(() => {});
  const loadSlips = () => api.get("/hr/payroll", { params: { month } }).then((r) => setSlips(r.data)).catch(() => {});
  const loadAdv = () => api.get("/hr/advances").then((r) => setAdvances(r.data)).catch(() => {});
  useEffect(() => { loadStaff(); loadAdv(); }, []);
  useEffect(() => { loadSlips(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  async function generate() {
    try { const { data } = await api.post("/hr/payroll/generate", { month }); toast.success(`${data.created} payslip(s) generated${data.skipped_without_salary ? `, ${data.skipped_without_salary} staff without salary setup` : ""}`); loadSlips(); loadAdv(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function decide(id, status) {
    try { await api.patch(`/hr/advances/${id}`, { status }); toast.success(`Advance ${status}`); loadAdv(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  const total = slips.reduce((a, b) => a + b.net, 0);
  const paid = slips.filter((s) => s.status === "paid").reduce((a, b) => a + b.net, 0);
  const ADV = { pending: "bg-amber-100 text-amber-800", approved: "bg-violet-100 text-violet-800", rejected: "bg-rose-100 text-rose-800", deducted: "bg-emerald-100 text-emerald-800" };

  return (
    <AppShell>
      <PageHeader eyebrow="HR" title="Payroll & HR" subtitle="Net = Basic + Allowances + Incentives + Overtime − Deductions − Advances" />
      <Tabs defaultValue="payroll">
        <TabsList className="bg-violet-100 flex-wrap h-auto">
          <TabsTrigger data-testid="hr-tab-payroll" value="payroll">Monthly Payroll</TabsTrigger>
          <TabsTrigger data-testid="hr-tab-setup" value="setup">Salary Setup</TabsTrigger>
          <TabsTrigger data-testid="hr-tab-advances" value="advances">Advances</TabsTrigger>
          <TabsTrigger data-testid="hr-tab-reports" value="reports">Reports</TabsTrigger>
        </TabsList>
        <TabsContent value="payroll" className="mt-5 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div><Label className="text-xs">Month</Label><Input data-testid="hr-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1 w-44 bg-white" /></div>
            <Button data-testid="hr-generate" onClick={generate} className="bg-violet-600 hover:bg-violet-700"><Play size={15} className="mr-1" /> Generate payroll</Button>
            <div className="ml-auto text-sm">Total <b>{inr(total)}</b> • Paid <b className="text-emerald-700">{inr(paid)}</b> • Pending <b className="text-rose-700">{inr(total - paid)}</b></div>
          </div>
          <div className="bg-white rounded-2xl border border-violet-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-violet-50"><tr><th className="text-left px-3 py-2.5">Staff</th><th className="text-right px-3">Gross</th><th className="text-right px-3">Deductions</th><th className="text-right px-3">Advance</th><th className="text-right px-3">Net</th><th className="px-3">Status</th><th className="px-3">Paid on / method</th><th /></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {slips.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-500">No payroll for this month yet.</td></tr>}
                {slips.map((p) => (
                  <tr key={p.id} data-testid={`hr-slip-row-${p.id}`}>
                    <td className="px-3 py-2.5"><div className="font-semibold">{p.name}</div><div className="text-xs text-slate-500">{ROLE_LABELS[p.role]} • leave {p.leave_days}d{p.absence_deduction !== undefined ? ` • absent ${p.absent_days}d, half ${p.half_days}d, late ${p.late_days || 0}` : ""}</div></td>
                    <td className="text-right px-3">{inr(p.gross)}</td><td className="text-right px-3">{inr(p.total_deductions)}</td><td className="text-right px-3">{inr(p.advance_deduction)}</td>
                    <td className="text-right px-3 font-bold">{inr(p.net)}</td>
                    <td className="px-3 text-center"><Badge className={p.status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{p.status}</Badge></td>
                    <td className="px-3 text-xs text-center">{p.payment_date ? `${fmtDate(p.payment_date)} • ${p.payment_method.toUpperCase()}` : "—"}</td>
                    <td className="px-3"><SlipActions p={p} onDone={loadSlips} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="setup" className="mt-5">
          <div className="bg-white rounded-2xl border border-violet-100 divide-y divide-slate-100">
            {staff.map((s) => (
              <div key={s.id} data-testid={`hr-staff-${s.id}`} className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1"><div className="font-semibold text-slate-900">{s.name}</div><div className="text-xs text-slate-500">{ROLE_LABELS[s.role]}</div></div>
                <div className="text-sm text-right">{s.setup ? <>Basic <b>{inr(s.setup.basic)}</b> • Allow. {inr(s.setup.allowances)}</> : <span className="text-slate-400">Not set</span>}</div>
                {canSetup && <Button data-testid={`hr-edit-${s.id}`} size="sm" variant="outline" onClick={() => setEditing(s)}><Pencil size={14} /></Button>}
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="advances" className="mt-5">
          <div className="bg-white rounded-2xl border border-violet-100 divide-y divide-slate-100">
            {advances.length === 0 && <p className="py-8 text-center text-sm text-slate-500">No advance requests.</p>}
            {advances.map((a) => (
              <div key={a.id} data-testid={`hr-advance-${a.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="flex-1"><div className="font-semibold">{a.name} <span className="text-xs text-slate-500">• {ROLE_LABELS[a.role]}</span></div><div className="text-xs text-slate-500">{inr(a.amount)} • {a.reason} • {fmtDate(a.created_at)}{a.deducted_month ? ` • deducted in ${a.deducted_month}` : ""}</div></div>
                <Badge className={`${ADV[a.status]} capitalize`}>{a.status}</Badge>
                {canDecide && a.status === "pending" && <>
                  <Button data-testid={`hr-adv-approve-${a.id}`} size="sm" onClick={() => decide(a.id, "approved")} className="bg-emerald-600 hover:bg-emerald-700"><Check size={14} /></Button>
                  <Button data-testid={`hr-adv-reject-${a.id}`} size="sm" variant="outline" onClick={() => decide(a.id, "rejected")} className="text-rose-600"><X size={14} /></Button>
                </>}
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-2">Approved advances are deducted automatically in the next generated payroll. Staff leave is approved in Leave; approved days beyond the free leaves are deducted.</p>
        </TabsContent>
        <TabsContent value="reports" className="mt-5"><Reports /></TabsContent>
      </Tabs>
      <SetupDialog staff={editing} onClose={() => setEditing(null)} onSaved={loadStaff} />
    </AppShell>
  );
}
